/*
=========================================================
* SiteFooter — shared footer for all marketing + support pages
=========================================================
*
* Single source of truth for the footer rendered on every public page of
* both brands (RingFoods + RingOperator). Previously the homepage had an
* inline footer while Privacy/Terms/Affiliates used a different
* `DefaultFooter + footer.routes.js` pair — the two drifted. Having one
* component per site means we can't get out of sync again.
*
* All brand strings (logo, description, social URLs, sister-site link)
* resolve from `src/constants/brand.js` at build time via REACT_APP_BRAND.
*/

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import useSignupUrl from "hooks/useSignupUrl";

import ringfoodsLogo from "assets/images/logos/ringfoods_logo.svg";
import ringoperatorLogo from "assets/images/logos/ringoperator_logo.svg";

import {
  BRAND_KEY,
  MARKETING_URL,
  SISTER_SITE_URL,
} from "constants/brand";

// Per-brand footer profile. Everything that differs between ringfoods and
// ringoperator lives in one place so the JSX below stays uniform.
const FOOTER_PROFILE = BRAND_KEY === "ringoperator"
  ? {
      logo: ringoperatorLogo,
      altText: "RingOperator",
      description:
        "AI phone agent for service businesses. Answer every call, book every appointment, qualify every lead.",
      supportEmail: "support@ringoperator.com",
      socials: {
        x: "https://x.com/ringoperator",
        instagram: "https://www.instagram.com/ringoperator",
        youtube: "https://www.youtube.com/@ringoperator",
        linkedin: "https://www.linkedin.com/company/ringoperator",
      },
      sisterLabel: "RingFoods for restaurants",
      // Product column — operator-specific anchors.
      productLinks: [
        { label: "Features", href: "#features" },
        { label: "How It Works", href: "#how-it-works" },
        { label: "Pricing", href: "#pricing" },
        { label: "FAQ", href: "#faq" },
        { label: "Affiliate Program", href: "/affiliates" },
        { label: "Blog", href: "/blog" },
      ],
    }
  : {
      logo: ringfoodsLogo,
      altText: "RingFoods",
      description:
        "AI-powered stress shield for restaurants. Every call answered, every caller respected.",
      supportEmail: "support@ringfoods.com",
      socials: {
        x: "https://x.com/ringfoods",
        instagram: "https://www.instagram.com/ringfoodsdotcom",
        youtube: "https://www.youtube.com/@ringfoodsofficial",
        linkedin: "https://www.linkedin.com/in/ring-foods-5841a83b9",
      },
      sisterLabel: "RingOperator for businesses",
      productLinks: [
        { label: "Features", href: "#features" },
        { label: "How It Works", href: "#how-it-works" },
        { label: "Pricing", href: "#pricing" },
        { label: "Integrations", href: "#integrations" },
        // The three keyword landing pages. They live in sitemap.xml, but a page
        // reachable only from the sitemap is an orphan — sitewide footer links
        // are what actually give them internal PageRank and a crawl path.
        // Anchor text is the target keyword on purpose.
        { label: "AI Voice Agent for Restaurants", href: "/ai-voice-agent-for-restaurants" },
        { label: "Restaurant Phone Answering", href: "/restaurant-phone-answering" },
        { label: "AI Restaurant Reservations", href: "/ai-restaurant-reservations" },
        { label: "Affiliate Program", href: "/affiliates" },
        { label: "Blog", href: "/blog" },
      ],
    };

// Anchors to sections on the homepage. If the footer is rendered on a
// non-homepage route (e.g., /pages/support/privacy), the in-page `#features`
// hash wouldn't resolve — prefix with the homepage path so clicks go
// through to the right section. On the homepage, the prefix is a no-op
// because the URL already starts with the marketing host root.
function resolveHref(href, isHomepage) {
  if (!href || href[0] !== "#") return href;
  return isHomepage ? href : `/${href}`;
}

export default function SiteFooter({ isHomepage = false }) {
  const signupUrl = useSignupUrl();

  return (
    <MKBox
      component="footer"
      sx={{
        backgroundColor: "#1A1A1A",
        pt: { xs: 6, md: 8 },
        pb: { xs: 4, md: 5 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Container>
        <Grid container spacing={4}>
          {/* Brand + CTA */}
          <Grid item xs={12} md={4}>
            <MKBox component="a" href={MARKETING_URL} sx={{ display: "inline-block", mb: 1 }}>
              <MKBox
                component="img"
                src={FOOTER_PROFILE.logo}
                alt={FOOTER_PROFILE.altText}
                width={120}
                height={32}
                sx={{ height: 32, filter: "brightness(0) invert(1)" }}
              />
            </MKBox>
            {/* Parent-company attribution — required by Google OAuth reviewers
                to match the consent-screen app name ("Ring AI Inc."). */}
            <MKTypography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.55)", display: "block", mb: 2, letterSpacing: "0.02em" }}
            >
              Made by Ring AI Inc.
            </MKTypography>
            <MKTypography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.6)", mb: 2, lineHeight: 1.7, maxWidth: 300 }}
            >
              {FOOTER_PROFILE.description}
            </MKTypography>
            <MKBox sx={{ display: "flex", gap: 1.5, mb: 3 }}>
              <MKBox
                component="a"
                href={FOOTER_PROFILE.socials.x}
                target="_blank"
                rel="noopener"
                aria-label={`Follow ${FOOTER_PROFILE.altText} on X`}
                sx={{ color: "rgba(255,255,255,0.6)", "&:hover": { color: "white" }, transition: "color 0.2s", display: "flex" }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </MKBox>
              <MKBox
                component="a"
                href={FOOTER_PROFILE.socials.instagram}
                target="_blank"
                rel="noopener"
                aria-label={`Follow ${FOOTER_PROFILE.altText} on Instagram`}
                sx={{ color: "rgba(255,255,255,0.6)", "&:hover": { color: "white" }, transition: "color 0.2s", display: "flex" }}
              >
                <InstagramIcon sx={{ fontSize: 22 }} />
              </MKBox>
              <MKBox
                component="a"
                href={FOOTER_PROFILE.socials.youtube}
                target="_blank"
                rel="noopener"
                aria-label={`Subscribe to ${FOOTER_PROFILE.altText} on YouTube`}
                sx={{ color: "rgba(255,255,255,0.6)", "&:hover": { color: "white" }, transition: "color 0.2s", display: "flex" }}
              >
                <YouTubeIcon sx={{ fontSize: 22 }} />
              </MKBox>
              <MKBox
                component="a"
                href={FOOTER_PROFILE.socials.linkedin}
                target="_blank"
                rel="noopener"
                aria-label={`Connect with ${FOOTER_PROFILE.altText} on LinkedIn`}
                sx={{ color: "rgba(255,255,255,0.6)", "&:hover": { color: "white" }, transition: "color 0.2s", display: "flex" }}
              >
                <LinkedInIcon sx={{ fontSize: 22 }} />
              </MKBox>
            </MKBox>
            <MKButton
              variant="contained"
              size="small"
              onClick={() => (window.location.href = signupUrl)}
              sx={{
                backgroundColor: "#FFFFFF !important",
                color: "#1A1A1A !important",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { backgroundColor: "#F5F5F5 !important" },
              }}
            >
              Start Free Trial
            </MKButton>
          </Grid>

          {/* Product Links */}
          <Grid item xs={6} sm={3} md={2}>
            <MKTypography variant="body2" fontWeight={700} sx={{ color: "white", mb: 2, textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: 1 }}>
              Product
            </MKTypography>
            {FOOTER_PROFILE.productLinks.map((link) => (
              <MKTypography
                key={link.label}
                component="a"
                href={resolveHref(link.href, isHomepage)}
                variant="body2"
                sx={{
                  display: "block",
                  color: "rgba(255,255,255,0.6)",
                  mb: 1,
                  fontSize: "0.85rem",
                  textDecoration: "none",
                  "&:hover": { color: "white" },
                  transition: "color 0.2s",
                }}
              >
                {link.label}
              </MKTypography>
            ))}
          </Grid>

          {/* Support Links (includes sister-site cross-link) */}
          <Grid item xs={6} sm={3} md={2}>
            <MKTypography variant="body2" fontWeight={700} sx={{ color: "white", mb: 2, textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: 1 }}>
              Support
            </MKTypography>
            {[
              { label: "Contact Us", href: `mailto:${FOOTER_PROFILE.supportEmail}` },
              { label: "Request Demo", href: `mailto:${FOOTER_PROFILE.supportEmail}?subject=Demo%20Request` },
              // Sister-site cross-link — descriptive anchor for SEO entity
              // association (Ring AI Inc. parent company).
              { label: FOOTER_PROFILE.sisterLabel, href: SISTER_SITE_URL },
            ].map((link) => (
              <MKTypography
                key={link.label}
                component="a"
                href={link.href}
                variant="body2"
                sx={{
                  display: "block",
                  color: "rgba(255,255,255,0.6)",
                  mb: 1,
                  fontSize: "0.85rem",
                  textDecoration: "none",
                  "&:hover": { color: "white" },
                  transition: "color 0.2s",
                }}
              >
                {link.label}
              </MKTypography>
            ))}
          </Grid>

          {/* Legal Links */}
          <Grid item xs={6} sm={3} md={2}>
            <MKTypography variant="body2" fontWeight={700} sx={{ color: "white", mb: 2, textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: 1 }}>
              Legal
            </MKTypography>
            {[
              { label: "Privacy Policy", href: "/pages/support/privacy" },
              { label: "Terms & Conditions", href: "/pages/support/terms" },
              { label: "SMS Policy", href: "/sms-policy" },
            ].map((link) => (
              <MKTypography
                key={link.label}
                component="a"
                href={link.href}
                variant="body2"
                sx={{
                  display: "block",
                  color: "rgba(255,255,255,0.6)",
                  mb: 1,
                  fontSize: "0.85rem",
                  textDecoration: "none",
                  "&:hover": { color: "white" },
                  transition: "color 0.2s",
                }}
              >
                {link.label}
              </MKTypography>
            ))}
          </Grid>

          {/* Regions */}
          <Grid item xs={6} sm={3} md={2}>
            <MKTypography variant="body2" fontWeight={700} sx={{ color: "white", mb: 2, textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: 1 }}>
              Available In
            </MKTypography>
            {["USA", "Canada", "United Kingdom", "Australia", "New Zealand"].map((region) => (
              <MKTypography
                key={region}
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.6)", mb: 0.75, fontSize: "0.85rem" }}
              >
                {region}
              </MKTypography>
            ))}
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <MKBox
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.1)",
            mt: { xs: 4, md: 6 },
            pt: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", sm: "center" },
            gap: 1,
          }}
        >
          <MKTypography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem" }}>
            &copy; 2026 Ring AI Inc. All rights reserved.
          </MKTypography>
          <MKTypography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem" }}>
            {FOOTER_PROFILE.supportEmail}
          </MKTypography>
        </MKBox>
      </Container>
    </MKBox>
  );
}
